import React, { useEffect, useState } from "react";
import { Button, Box } from "@mui/material";
import FormField from "../FormComponent/FormField/FormField";
import styles from './practitioner.module.css'
import { useForm } from 'react-hook-form';
import { axiosProxyInstance } from "../../utils/request";
import cogoToast from "cogo-toast";
import { useJsApiLoader } from '@react-google-maps/api';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

const phoneRegExp = /^[0-9]\d*$/;

const validationSchema = yup.object({
    clinicName: yup
        .string()
        .required("Clinic name is required")
        .matches(/^[a-zA-Z0-9\s,.&()-]+$/, "Clinic name cannot contain special characters")
        .max(100, "Clinic name cannot exceed 100 characters")
        .test(
            "not-only-spaces",
            "This field cannot contain only blank spaces",
            (value) => value ? value.trim().length > 0 : true // Ensure non-blank content
        ),
    streetAddress: yup.string()
        .required('Street address is required')
        .test(
            "not-only-spaces",
            "This field cannot contain only blank spaces",
            (value) => value ? value.trim().length > 0 : true // Ensure non-blank content
        ),
    buildingName: yup.string()
        .test(
            "not-only-spaces",
            "This field cannot contain only blank spaces",
            (value) => value ? value.trim().length > 0 : true // Ensure non-blank content
        ),
    town: yup
        .mixed()
        .transform((value, originalValue) => {
            if (typeof originalValue === 'string') {
                return originalValue.trim() === '' ? undefined : originalValue;
            }
            return value;
        })
        .test(
            'is-valid-town',
            'Town/City is required',
            (value) => {
                // Accept either a non-empty string or an object with required props
                if (typeof value === 'string') {
                return value.trim().length > 0;
                }
                if (typeof value === 'object' && value !== null) {
                return 'name' in value || 'id' in value; // adjust as needed
                }
                return false;
            }
        )
        .required('Town/City is required'),
    postcode: yup.string()
        .required('Postcode is required')
        .matches(/^[a-zA-Z0-9\s,.&()-]+$/, "Clinic name cannot contain special characters")
        .test(
            "not-only-spaces",
            "This field cannot contain only blank spaces",
            (value) => value ? value.trim().length > 0 : true // Ensure non-blank content
        ),
    phone: yup
        .string()
        .required('Phone number is required')
        .matches(phoneRegExp, "Phone number is not valid"),
});

const Address = ({ sectionData, details }) => {
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_MAP_KEY,
        libraries: ['places'],
    });
    const [options, setOptions] = useState(null);
    const [defaultValues, setDefaultValues] = useState(null);
    const currentUserId = localStorage.getItem("LPCurrentUserId");

    const form = useForm({
        resolver: yupResolver(validationSchema),
        mode: "onBlur",
    });
    const { register, handleSubmit, control, formState: { errors, isDirty }, reset } = form;
    const allFields = sectionData.fields.flatMap(field =>
        field.fields ? field.fields : [field]
    );
    const clinicNameField = allFields.find(field => field.id === "clinicName");
    const streetAddressField = allFields.find(field => field.id === "streetAddress");
    const buildingNameField = allFields.find(field => field.id === "buildingName");
    const townField = allFields.find(field => field.id === "town");
    const postcodeField = allFields.find(field => field.id === "postcode");
    const phoneField = allFields.find(field => field.id === "phone");

    useEffect(() => {
        if (details) {
            const resData = {
                clinicName: details?.post_title,
                streetAddress: details?.data?.address_1,
                buildingName: details?.data?.address_2,
                town: details?.data?.location?.[0],
                postcode: details?.data?.postcode,
                phone: details?.data?.phone?.raw,
            };
            reset(resData);
            setDefaultValues(resData)
        }
    }, [details, reset]);

    const geocodeAddress = (address) => {
        return new Promise((resolve, reject) => {
            const geocoder = new window.google.maps.Geocoder();

            geocoder.geocode({ address }, (results, status) => {
            if (status === 'OK' && results[0]) {
                const location = results[0].geometry.location;
                resolve({ lat: location.lat(), lng: location.lng() });
            } else {
                console.error('Geocode error:', status);
                reject(status);
            }
            });
        });
    };
    const handleLinkButtonClick = async (field) => {
        try {
            const getLocationsResponse = await axiosProxyInstance.get(`/location?town=${field}`);
            const responseData = getLocationsResponse.data?.data;
            
            const optionPromises = responseData?.map(async (item) => {
                const fullAddress = `${item.n}, ${item.c}, ${item.r}, ${item.cc}`;
                try {
                    const coords = await geocodeAddress(fullAddress);
                    return {
                        label: item.n,
                        value: item.id,
                        lat: coords.lat,
                        lng: coords.lng,
                    };
                } catch (err) {
                    return {
                        label: item.n,
                        value: item.id,
                        lat: null,
                        lng: null,
                    };
                }
                
                // return { label: item.n, value: item?.id };
            });
            const options = await Promise.all(optionPromises);
            setOptions(options);
        } catch (error) {
            console.error('Error during signup:', error);
        } finally {
        }
    }
    const onSubmit = async (data) => {
        const payload = {
            btn_submit: true,
            listings: [
                {
                    listing_id: details?.ID,
                    title: data.clinicName,
                    address_1: data.streetAddress,
                    address_2: data.buildingName || '',
                    postcode: data.postcode,
                    telephone_1: data.phone,
                    location: typeof data.town === 'string' ? [data.town] : [data?.town?.value] || '',
                    specialization: (Object.values(details.data.services || {})),
                    treatments: Object.values(details.data.treatments || {}),
                    provideruid: currentUserId,
                    uid: currentUserId,
                    latitude: typeof data.town === 'string' ? '' : data?.town?.lat || '',
                    longitude: typeof data.town === 'string' ? '' : data?.town?.lng || ''
                }
            ]
        };
        
        try {
            const res = await axiosProxyInstance.post('/signup/2/', payload);
            cogoToast.success("Address Updated Successfully!");
        } catch (err) {
            console.error("Submission failed:", err);
        }
    }
    return (
        <Box>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box className='margin-top-15'>
                    <Box style={{ flex: "1" }}>
                        <FormField
                            field={clinicNameField}
                            register={register}
                            control={control}
                            errors={errors}
                        />
                        <FormField
                            field={streetAddressField}
                            register={register}
                            control={control}
                            errors={errors}
                        />
                        <FormField
                            field={buildingNameField}
                            register={register}
                            control={control}
                            errors={errors}
                        />
                        <Box className={styles.formFields}>
                            <FormField
                                onLinkButtonClick={(clickedField) =>
                                    handleLinkButtonClick(clickedField)
                                }
                                field={{ ...townField, options }}
                                register={register}
                                control={control}
                                errors={errors}
                            />
                            <FormField
                                field={postcodeField}
                                register={register}
                                control={control}
                                errors={errors}
                            />
                        </Box>
                        <FormField
                            field={phoneField}
                            register={register}
                            control={control}
                            errors={errors}
                        />

                        <Box className={styles.addressSubmitButtonContainer}>
                            <Button
                                disabled={!isDirty}
                                className={styles.primaryButton}
                                size="large"
                                type="submit"
                            >
                                Save Changes
                            </Button>
                            <Button
                                disabled={!isDirty}
                                className={styles.secondaryButton}
                                size="large"
                                onClick={() => reset(defaultValues)}
                            >
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </form>
        </Box>
    )
}
export default Address